import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  FileText, 
  Package, 
  Clock, 
  DollarSign, 
  TrendingUp, 
  AlertCircle,
  Users,
  Building2,
  CheckCircle2,
  ArrowRight,
  Eye,
  Send,
  Star,
  BarChart3,
  Receipt,
  ShieldCheck,
  FileCheck,
  Plus,
  Activity,
  Calendar,
  ChevronRight,
  Layers,
  Target,
  Award,
  ClipboardList,
  TrendingDown,
  Percent,
  Briefcase,
  Zap,
  ArrowUpRight,
  ArrowDownRight,
  CircleDot,
  Timer,
  MessageSquare,
  Bell,
  Sparkles,
  PieChart,
  Box,
  Lock,
  Fingerprint,
  BellRing
} from 'lucide-react';
import Navbar from '@/components/marketplace/Navbar';
import Sidebar from '@/components/marketplace/Sidebar';
import { DashboardSkeleton } from '@/components/marketplace/Skeletons';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { useWebAuthn } from '@/hooks/useWebAuthn';

// Mini Bar Chart Component
const MiniBarChart = ({ data, color = 'blue', height = 40 }) => {
  const max = Math.max(...data, 1);
  const colorMap = {
    blue: '#2563eb',
    emerald: '#10b981',
    amber: '#f59e0b',
    indigo: '#6366f1',
    violet: '#8b5cf6',
    rose: '#f43f5e',
  };
  return (
    <div className="flex items-end gap-1" style={{ height }}>
      {data.map((value, i) => (
        <div
          key={i}
          className="flex-1 rounded-t transition-all hover:opacity-80"
          style={{ 
            height: `${(value / max) * 100}%`, 
            minHeight: value > 0 ? '4px' : '0px',
            backgroundColor: colorMap[color] || colorMap.blue
          }}
        />
      ))}
    </div>
  );
};

// Premium Stat Card Component
const StatCard = ({ title, value, subtitle, icon: Icon, trend, trendValue, color = 'blue', onClick }) => {
  const gradientStyles = {
    blue: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
    emerald: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
    amber: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
    indigo: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)',
    violet: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)',
    rose: 'linear-gradient(135deg, #fff1f2 0%, #ffe4e6 100%)',
  };
  
  const iconColors = {
    blue: 'text-blue-600 bg-blue-200',
    emerald: 'text-emerald-600 bg-emerald-200',
    amber: 'text-amber-600 bg-amber-200',
    indigo: 'text-indigo-600 bg-indigo-200',
    violet: 'text-violet-600 bg-violet-200',
    rose: 'text-rose-600 bg-rose-200',
  };
  
  const accentColors = {
    blue: '#2563eb',
    emerald: '#10b981',
    amber: '#f59e0b',
    indigo: '#6366f1',
    violet: '#8b5cf6',
    rose: '#f43f5e',
  };
  
  return (
    <Card 
      className={`relative overflow-hidden border-0 shadow-sm hover:shadow-lg transition-all duration-300 ${onClick ? 'cursor-pointer group' : ''}`}
      onClick={onClick}
      data-testid={`stat-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
      style={{ background: gradientStyles[color] }}
    >
      {/* Accent Line */}
      <div 
        className="absolute top-0 left-0 right-0 h-1"
        style={{ backgroundColor: accentColors[color] }}
      />
      <CardContent className="p-4 sm:p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <p className="text-xs sm:text-sm font-medium text-slate-500 uppercase tracking-wide truncate">{title}</p>
            <p className="text-xl sm:text-3xl font-bold text-slate-900 mt-1 sm:mt-2 tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {value}
            </p>
            {subtitle && <p className="text-xs sm:text-sm text-slate-500 mt-0.5 sm:mt-1 truncate">{subtitle}</p>}
            {trend && (
              <div className={`flex items-center gap-1 mt-3 text-sm ${trend === 'up' ? 'text-emerald-600' : 'text-rose-600'}`}>
                {trend === 'up' ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                <span className="font-semibold">{trendValue}</span>
                <span className="text-slate-400">vs last month</span>
              </div>
            )}
          </div>
          <div className={`w-9 h-9 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center ${iconColors[color]} group-hover:scale-110 transition-transform duration-300`}>
            <Icon className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={2} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Premium Quick Action Card
const QuickActionCard = ({ title, description, icon: Icon, color = 'blue', onClick, badge }) => {
  const iconBgColors = {
    blue: 'bg-blue-600',
    emerald: 'bg-emerald-600',
    amber: 'bg-amber-500',
    indigo: 'bg-indigo-600',
    violet: 'bg-violet-600',
    rose: 'bg-rose-500',
  };
  
  return (
    <Card 
      className="border-0 shadow-sm hover:shadow-lg transition-all duration-300 cursor-pointer group bg-white"
      onClick={onClick}
      data-testid={`action-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <CardContent className="p-3 sm:p-5">
        <div className="flex items-center gap-3 sm:gap-4">
          <div className={`w-10 h-10 sm:w-12 sm:h-12 ${iconBgColors[color]} rounded-xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg shrink-0`}
               style={{ boxShadow: `0 8px 16px -4px ${color === 'blue' ? 'rgba(37, 99, 235, 0.3)' : color === 'violet' ? 'rgba(139, 92, 246, 0.3)' : 'rgba(16, 185, 129, 0.3)'}` }}>
            <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-slate-900">{title}</h3>
              {badge && (
                <span className="px-2 py-0.5 text-[10px] font-bold bg-gradient-to-r from-violet-500 to-purple-500 text-white rounded-full">
                  {badge}
                </span>
              )}
            </div>
            <p className="text-sm text-slate-500">{description}</p>
          </div>
          <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-slate-500 group-hover:translate-x-1 transition-all" />
        </div>
      </CardContent>
    </Card>
  );
};

// Premium Activity Item Component
const ActivityItem = ({ title, subtitle, status, amount, currency, onClick, icon: Icon }) => (
  <div
    onClick={onClick}
    className="p-4 bg-white border border-slate-100 rounded-xl hover:border-blue-200 hover:shadow-md cursor-pointer transition-all duration-200 group"
  >
    <div className="flex items-center gap-4">
      {Icon && (
        <div className="w-11 h-11 bg-slate-50 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-blue-50 transition-colors">
          <Icon className="w-5 h-5 text-slate-500 group-hover:text-blue-600 transition-colors" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-slate-900 truncate">{title}</p>
        <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>
      </div>
      <div className="text-right flex-shrink-0">
        {amount && (
          <p className="font-bold text-emerald-600" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
            {formatCurrency(amount, currency)}
          </p>
        )}
        {status && (
          <Badge className={`${getStatusColor(status)} border mt-1 font-medium`}>
            {status?.replace(/_/g, ' ')}
          </Badge>
        )}
      </div>
    </div>
  </div>
);

const API = `${process.env.REACT_APP_BACKEND_URL}/api/marketplace`;

// Currency symbols mapping
const CURRENCY_SYMBOLS = {
  USD: '$',
  SAR: 'ر.س',
  EGP: 'E£',
  AED: 'د.إ',
  EUR: '€'
};

// Format currency with symbol
const formatCurrency = (amount, currency = 'USD') => {
  if (!amount && amount !== 0) return 'N/A';
  const symbol = CURRENCY_SYMBOLS[currency] || currency;
  return `${symbol}${parseFloat(amount).toLocaleString()}`;
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

// Status color mapping
const getStatusColor = (status) => {
  const colors = {
    draft: 'bg-slate-100 text-slate-700 border-slate-200',
    sent: 'bg-blue-50 text-blue-700 border-blue-200',
    submitted: 'bg-amber-50 text-amber-700 border-amber-200',
    accepted: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    rejected: 'bg-red-50 text-red-700 border-red-200',
    in_progress: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    pending_execution: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    po_pending: 'bg-amber-50 text-amber-700 border-amber-200',
    po_uploaded: 'bg-blue-50 text-blue-700 border-blue-200',
    executed: 'bg-purple-50 text-purple-700 border-purple-200',
    invoiced: 'bg-cyan-50 text-cyan-700 border-cyan-200',
    payment_pending: 'bg-orange-50 text-orange-700 border-orange-200',
    completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    active: 'bg-emerald-50 text-emerald-700 border-emerald-200'
  };
  return colors[status] || 'bg-slate-100 text-slate-700 border-slate-200';
};

export default function Dashboard() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [rfqs, setRfqs] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [orders, setOrders] = useState([]);
  const [adminStats, setAdminStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [orderStats, setOrderStats] = useState(null);
  const [badges, setBadges] = useState({});

  // Mobile setup hooks
  const { supported: pushSupported, subscribed: pushSubscribed, subscribe: pushSubscribe, loading: pushLoading } = usePushNotifications();
  const { supported: bioSupported, checkPlatformAuthenticator, register: bioRegister, loading: bioLoading } = useWebAuthn();
  const [platformAuthAvailable, setPlatformAuthAvailable] = useState(false);
  const [hasBioCredential, setHasBioCredential] = useState(false);
  const [setupDismissed, setSetupDismissed] = useState(() => sessionStorage.getItem('setup_dismissed') === '1');

  useEffect(() => {
    if (bioSupported) {
      checkPlatformAuthenticator().then(setPlatformAuthAvailable);
      const token = localStorage.getItem('token');
      if (token) {
        axios.get(`${API}/webauthn/credentials`, { headers: { Authorization: `Bearer ${token}` } })
          .then(res => setHasBioCredential(res.data?.length > 0))
          .catch(() => {});
      }
    }
  }, [bioSupported]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/marketplace/login', { replace: true });
      return;
    }
    if (user) {
      fetchDashboardData();
      fetchBadges();
    }
  }, [user, authLoading]);

  const fetchBadges = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/dashboard/badges`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBadges(data);
    } catch { }
  };

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };
      
      if (user?.user_type === 'admin') {
        // Admin dashboard data - more comprehensive
        const [statsRes, rfqsRes, ordersRes, vendorsRes, customersRes, orderStatsRes] = await Promise.all([
          axios.get(`${API}/dashboard/stats`, config).catch(() => ({ data: {} })),
          axios.get(`${API}/admin/rfqs`, config).catch(() => ({ data: [] })),
          axios.get(`${API}/admin/orders`, config).catch(() => ({ data: [] })),
          axios.get(`${API}/admin/vendors`, config).catch(() => ({ data: [] })),
          axios.get(`${API}/admin/customers`, config).catch(() => ({ data: [] })),
          axios.get(`${API}/orders/statistics`, config).catch(() => ({ data: {} }))
        ]);
        
        setStats(statsRes.data);
        setRfqs(Array.isArray(rfqsRes.data) ? rfqsRes.data.slice(0, 6) : []);
        setOrders(Array.isArray(ordersRes.data) ? ordersRes.data.slice(0, 6) : []);
        setOrderStats(orderStatsRes.data);
        
        // Calculate admin stats with more detail
        const vendors = Array.isArray(vendorsRes.data) ? vendorsRes.data : [];
        const customers = Array.isArray(customersRes.data) ? customersRes.data : [];
        const allOrders = Array.isArray(ordersRes.data) ? ordersRes.data : [];
        
        setAdminStats({
          totalVendors: vendors.length,
          activeVendors: vendors.filter(v => v.is_active).length,
          totalCustomers: customers.length,
          activeCustomers: customers.filter(c => c.is_active).length,
          pendingApprovals: vendors.filter(v => v.verification_status === 'pending' || !v.is_approved).length,
          totalOrders: allOrders.length,
          activeOrders: allOrders.filter(o => !['completed', 'cancelled'].includes(o.order_status)).length,
          completedOrders: allOrders.filter(o => o.order_status === 'completed').length,
          totalRevenue: allOrders.filter(o => o.order_status === 'completed').reduce((sum, o) => sum + (o.total_amount || 0), 0),
          totalCommission: allOrders.filter(o => o.order_status === 'completed').reduce((sum, o) => sum + (o.commission_amount || 0), 0)
        });
      } else {
        // Brand Manager or Vendor dashboard data - enhanced
        const [statsRes, rfqsRes, quotationsRes, ordersRes, notifRes] = await Promise.all([
          axios.get(`${API}/dashboard/stats`, config).catch(() => ({ data: {} })),
          axios.get(`${API}/rfq`, config).catch(() => ({ data: [] })),
          axios.get(`${API}/quotations`, config).catch(() => ({ data: [] })),
          axios.get(`${API}/orders/my-orders`, config).catch(() => ({ data: [] })),
          axios.get(`${API}/notifications`, config).catch(() => ({ data: [] }))
        ]);
        
        setStats(statsRes.data);
        setRfqs(Array.isArray(rfqsRes.data) ? rfqsRes.data.slice(0, 6) : []);
        setQuotations(Array.isArray(quotationsRes.data) ? quotationsRes.data.slice(0, 6) : []);
        setOrders(Array.isArray(ordersRes.data) ? ordersRes.data.slice(0, 6) : []);
        setNotifications(Array.isArray(notifRes.data) ? notifRes.data.filter(n => !n.is_read).slice(0, 5) : []);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <DashboardSkeleton />;
  }

  // Brand Manager Dashboard
  if (user?.user_type === 'brand_manager') {
    // Calculate additional metrics
    const pendingQuotes = quotations.filter(q => q.status === 'submitted' || q.status === 'pending').length;
    const activeOrders = orders.filter(o => !['completed', 'cancelled'].includes(o.order_status)).length;
    const completedOrders = orders.filter(o => o.order_status === 'completed').length;
    const totalSpend = orders.filter(o => o.order_status === 'completed').reduce((sum, o) => sum + (o.total_amount || 0), 0);
    
    // Order status breakdown for mini chart
    const ordersByStatus = {
      po_pending: orders.filter(o => o.order_status === 'po_pending').length,
      in_progress: orders.filter(o => ['po_uploaded', 'pending_execution'].includes(o.order_status)).length,
      executed: orders.filter(o => ['executed', 'invoiced'].includes(o.order_status)).length,
      completed: orders.filter(o => o.order_status === 'completed').length,
    };
    
    return (
      <div className="min-h-screen bg-slate-100" data-testid="brand-manager-dashboard">
        <Navbar />
        <div className="flex">
          <Sidebar />
          <main className="flex-1 p-4 sm:p-6 lg:p-8 pb-20 lg:pb-8">
            <div className="max-w-7xl mx-auto space-y-8">
              {/* Premium Welcome Header */}
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                <div>
                  <p className="text-sm font-medium text-blue-600 uppercase tracking-wider mb-1">Dashboard</p>
                  <h1 className="text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    Welcome back, {user?.full_name?.split(' ')[0]}
                  </h1>
                  <p className="text-slate-500 mt-2">
                    {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Button 
                    variant="outline"
                    onClick={() => navigate('/marketplace/multimodal-rfq')}
                    className="h-11 px-5 rounded-xl border-slate-200 hover:border-blue-300 hover:bg-blue-50 transition-all"
                  >
                    <Layers className="w-4 h-4 mr-2" />
                    Multimodal RFP
                  </Button>
                  <Button 
                    onClick={() => navigate('/marketplace/request-service')}
                    className="h-11 px-6 rounded-xl bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-600/25 hover:shadow-blue-600/40 transition-all"
                    data-testid="new-rfq-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    New RFP
                  </Button>
                </div>
              </div>

              {/* Mobile Setup Banner - Push Notifications & Biometric */}
              {!setupDismissed && ((!pushSubscribed && pushSupported) || (platformAuthAvailable && !hasBioCredential)) && (
                <Card className="lg:hidden border-0 shadow-lg bg-gradient-to-r from-blue-50 to-indigo-50 overflow-hidden" data-testid="mobile-setup-banner">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center shrink-0">
                        <Zap className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-900 text-sm">Enhance Your Mobile Experience</p>
                        <p className="text-xs text-slate-500 mt-0.5">Enable these features for a faster, app-like experience</p>
                        <div className="flex flex-wrap gap-2 mt-3">
                          {!pushSubscribed && pushSupported && (
                            <Button
                              size="sm"
                              disabled={pushLoading}
                              onClick={async () => {
                                const ok = await pushSubscribe();
                                if (ok) { /* toast shown by hook */ }
                              }}
                              className="h-8 text-xs rounded-lg bg-blue-600 hover:bg-blue-700"
                              data-testid="enable-push-btn"
                            >
                              <BellRing className="w-3.5 h-3.5 mr-1.5" />
                              {pushLoading ? 'Enabling...' : 'Enable Notifications'}
                            </Button>
                          )}
                          {platformAuthAvailable && !hasBioCredential && (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={bioLoading}
                              onClick={async () => {
                                const res = await bioRegister();
                                if (res.success) setHasBioCredential(true);
                              }}
                              className="h-8 text-xs rounded-lg border-blue-300 text-blue-700 hover:bg-blue-50"
                              data-testid="enable-biometric-btn"
                            >
                              <Fingerprint className="w-3.5 h-3.5 mr-1.5" />
                              {bioLoading ? 'Setting up...' : 'Enable Face ID'}
                            </Button>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => { setSetupDismissed(true); sessionStorage.setItem('setup_dismissed', '1'); }}
                        className="text-slate-400 hover:text-slate-600 p-1"
                        data-testid="dismiss-setup-btn"
                      >
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                      </button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Mobile Quick Actions with Badges */}
              <div className="grid grid-cols-4 gap-2 lg:hidden" data-testid="mobile-quick-actions">
                <button
                  onClick={() => navigate('/marketplace/request-service')}
                  className="flex flex-col items-center gap-1 p-3 bg-white rounded-xl shadow-sm border border-slate-100 active:scale-95 transition-transform"
                  data-testid="quick-action-create-rfq"
                >
                  <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Plus className="w-5 h-5 text-blue-600" />
                  </div>
                  <span className="text-[10px] font-medium text-slate-600">New RFP</span>
                </button>
                <button
                  onClick={() => navigate('/marketplace/my-rfqs')}
                  className="relative flex flex-col items-center gap-1 p-3 bg-white rounded-xl shadow-sm border border-slate-100 active:scale-95 transition-transform"
                  data-testid="quick-action-view-rfqs"
                >
                  <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                    <FileText className="w-5 h-5 text-amber-600" />
                  </div>
                  {badges.rfqs > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">{badges.rfqs}</span>
                  )}
                  <span className="text-[10px] font-medium text-slate-600">My RFPs</span>
                </button>
                <button
                  onClick={() => navigate('/marketplace/messages')}
                  className="relative flex flex-col items-center gap-1 p-3 bg-white rounded-xl shadow-sm border border-slate-100 active:scale-95 transition-transform"
                  data-testid="quick-action-messages"
                >
                  <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-emerald-600" />
                  </div>
                  {badges.messages > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">{badges.messages}</span>
                  )}
                  <span className="text-[10px] font-medium text-slate-600">Messages</span>
                </button>
                <button
                  onClick={() => navigate('/marketplace/notifications')}
                  className="relative flex flex-col items-center gap-1 p-3 bg-white rounded-xl shadow-sm border border-slate-100 active:scale-95 transition-transform"
                  data-testid="quick-action-notifications"
                >
                  <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center">
                    <Bell className="w-5 h-5 text-violet-600" />
                  </div>
                  {badges.notifications > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">{badges.notifications}</span>
                  )}
                  <span className="text-[10px] font-medium text-slate-600">Alerts</span>
                </button>
              </div>

              {/* Alerts Section */}
              {(pendingQuotes > 0 || notifications.length > 0) && (
                <Card className="border-0 shadow-lg bg-gradient-to-r from-amber-50 to-orange-50 overflow-hidden">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-amber-100 rounded-2xl flex items-center justify-center">
                        <Bell className="w-6 h-6 text-amber-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-amber-900">
                          {pendingQuotes > 0 
                            ? `You have ${pendingQuotes} quotation${pendingQuotes > 1 ? 's' : ''} awaiting review`
                            : `You have ${notifications.length} new notification${notifications.length > 1 ? 's' : ''}`}
                        </p>
                        <p className="text-sm text-amber-700 mt-0.5">Review and take action to keep your projects moving</p>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="h-10 px-4 rounded-xl border-amber-300 text-amber-700 hover:bg-amber-100 bg-white/50"
                        onClick={() => navigate(pendingQuotes > 0 ? '/marketplace/quotations' : '/marketplace/notifications')}
                      >
                        Review Now
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Key Metrics Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                <StatCard
                  title="Active RFPs"
                  value={stats?.active_rfqs || stats?.pending_requests || rfqs.filter(r => r.status === 'sent').length}
                  subtitle="Awaiting quotations"
                  icon={FileText}
                  color="blue"
                  onClick={() => navigate('/marketplace/my-rfqs')}
                />
                <StatCard
                  title="Pending Quotations"
                  value={pendingQuotes}
                  subtitle="Needs your review"
                  icon={Receipt}
                  color="amber"
                  onClick={() => navigate('/marketplace/quotations')}
                />
                <StatCard
                  title="Orders In Progress"
                  value={activeOrders}
                  subtitle="Being executed"
                  icon={Package}
                  color="indigo"
                  onClick={() => navigate('/marketplace/orders')}
                />
                <StatCard
                  title="Total Completed"
                  value={completedOrders}
                  subtitle={`${formatCurrency(totalSpend)} total spend`}
                  icon={CheckCircle2}
                  color="emerald"
                  onClick={() => navigate('/marketplace/orders')}
                />
              </div>

              {/* Order Pipeline & Quick Actions */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Order Pipeline */}
                <Card className="lg:col-span-2 border-0 shadow-lg">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-xl font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Order Pipeline</CardTitle>
                        <CardDescription>Current order status breakdown</CardDescription>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => navigate('/marketplace/orders')} className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-xl">
                        View All <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                      {[
                        { label: 'PO Pending', count: ordersByStatus.po_pending, color: 'amber', icon: Clock },
                        { label: 'In Progress', count: ordersByStatus.in_progress, color: 'blue', icon: Activity },
                        { label: 'Executed', count: ordersByStatus.executed, color: 'indigo', icon: CheckCircle2 },
                        { label: 'Completed', count: ordersByStatus.completed, color: 'emerald', icon: Award },
                      ].map((item, idx) => (
                        <div key={idx} className="text-center p-5 bg-slate-50 rounded-2xl hover:bg-slate-100 transition-colors cursor-pointer">
                          <div className={`w-12 h-12 mx-auto mb-3 bg-${item.color}-100 rounded-xl flex items-center justify-center`}>
                            <item.icon className={`w-6 h-6 text-${item.color}-600`} />
                          </div>
                          <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{item.count}</p>
                          <p className="text-xs text-slate-500 mt-1 font-medium uppercase tracking-wide">{item.label}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Quick Actions */}
                <Card className="border-0 shadow-lg">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xl font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Quick Actions</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <QuickActionCard
                      title="Request Service"
                      description="Create a new RFP"
                      icon={Plus}
                      color="blue"
                      onClick={() => navigate('/marketplace/request-service')}
                    />
                    <QuickActionCard
                      title="AI Event Design"
                      description="Visualize before commit"
                      icon={Sparkles}
                      color="violet"
                      badge="AI"
                      onClick={() => navigate('/marketplace/ai-design')}
                    />
                    <QuickActionCard
                      title="View Reports"
                      description="Analytics & insights"
                      icon={BarChart3}
                      color="emerald"
                      onClick={() => navigate('/marketplace/reports')}
                    />
                  </CardContent>
                </Card>
              </div>

              {/* Recent Activity Section */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Recent RFPs */}
                <Card className="border-0 shadow-lg">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-xl font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Recent RFPs</CardTitle>
                        <CardDescription>Your latest requests</CardDescription>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => navigate('/marketplace/my-rfqs')} className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-xl">
                        View All <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {rfqs.length === 0 ? (
                      <div className="text-center py-10">
                        <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                          <FileText className="w-8 h-8 text-slate-400" />
                        </div>
                        <p className="text-slate-600 font-medium">No RFPs yet</p>
                        <p className="text-sm text-slate-400 mt-1">Create your first request for quotation</p>
                        <Button size="sm" className="mt-4 rounded-xl" onClick={() => navigate('/marketplace/request-service')}>
                          Create Your First RFP
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {rfqs.slice(0, 4).map((rfq) => (
                          <ActivityItem
                            key={rfq.id}
                            title={rfq.title}
                            subtitle={`${rfq.rfq_number} • ${formatDate(rfq.created_at)}`}
                            status={rfq.status}
                            icon={FileText}
                            onClick={() => navigate(`/marketplace/rfq/${rfq.id}`)}
                          />
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Pending Quotations */}
                <Card className="border-0 shadow-lg">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-xl font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Pending Quotations</CardTitle>
                        <CardDescription>Awaiting your review</CardDescription>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => navigate('/marketplace/quotations')} className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-xl">
                        View All <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {quotations.length === 0 ? (
                      <div className="text-center py-10">
                        <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                          <Receipt className="w-8 h-8 text-slate-400" />
                        </div>
                        <p className="text-slate-600 font-medium">No quotations yet</p>
                        <p className="text-sm text-slate-400 mt-1">Quotations will appear here when vendors respond</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {quotations.slice(0, 4).map((quote) => (
                          <ActivityItem
                            key={quote.id}
                            title={quote.rfq_title || quote.vendor_company_name || 'Untitled RFP'}
                            subtitle={`${quote.quotation_number} • ${quote.delivery_timeline || 'Timeline TBD'}`}
                            status={quote.status}
                            amount={quote.total_amount}
                            currency={quote.currency}
                            icon={Receipt}
                            onClick={() => navigate('/marketplace/quotations')}
                          />
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Active Orders Table */}
              {orders.length > 0 && (
                <Card className="border-0 shadow-lg">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-xl font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Active Orders</CardTitle>
                        <CardDescription>Orders requiring attention</CardDescription>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => navigate('/marketplace/orders')} className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-xl">
                        View All <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-slate-200 bg-slate-50/50">
                            <th className="text-left py-4 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Order</th>
                            <th className="text-left py-4 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Vendor</th>
                            <th className="text-left py-4 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Amount</th>
                            <th className="text-left py-4 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                            <th className="text-left py-4 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Next Action</th>
                            <th className="text-right py-4 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {orders.slice(0, 5).map((order) => (
                            <tr key={order.id} className="border-b border-slate-100 hover:bg-blue-50/30 transition-colors">
                              <td className="py-4 px-4">
                                <p className="font-semibold text-slate-900">{order.rfq_title || order.title || 'Untitled RFP'}</p>
                                <p className="text-xs text-slate-500 mt-0.5">{order.order_number} • {formatDate(order.created_at)}</p>
                              </td>
                              <td className="py-4 px-4">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/marketplace/vendor/${order.vendor_company_id || order.vendor_id}`);
                                  }}
                                  className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                                >
                                  {order.vendor_company_name || 'View Vendor'}
                                </button>
                              </td>
                              <td className="py-4 px-4">
                                <span className="font-bold text-slate-900" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                                  {formatCurrency(order.total_amount)}
                                </span>
                              </td>
                              <td className="py-4 px-4">
                                <Badge className={`${getStatusColor(order.order_status)} border font-medium`}>
                                  {order.order_status?.replace(/_/g, ' ')}
                                </Badge>
                              </td>
                              <td className="py-4 px-4 text-sm text-slate-600">
                                {order.order_status === 'po_pending' && <span className="text-amber-600 font-medium">Upload PO</span>}
                                {order.order_status === 'invoiced' && <span className="text-blue-600 font-medium">Process Payment</span>}
                                {order.order_status === 'completed' && !order.vendor_rating && <span className="text-violet-600 font-medium">Rate Vendor</span>}
                                {!['po_pending', 'invoiced', 'completed'].includes(order.order_status) && <span className="text-slate-400">Awaiting Vendor</span>}
                              </td>
                              <td className="py-4 px-4 text-right">
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  className="rounded-xl hover:bg-blue-100"
                                  onClick={() => navigate(`/marketplace/orders/${order.id}`)}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Team Performance (Parent Users Only) */}
              {user?.is_parent && stats?.sub_users_stats && stats.sub_users_stats.length > 0 && (
                <Card className="border-0 shadow-lg">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2 text-xl font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                          <Users className="w-5 h-5" />
                          Team Performance
                        </CardTitle>
                        <CardDescription>Activity breakdown by team member</CardDescription>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => navigate('/marketplace/team')} className="rounded-xl">
                        Manage Team
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-slate-200 bg-slate-50/50">
                            <th className="text-left py-4 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Team Member</th>
                            <th className="text-center py-4 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">RFPs Created</th>
                            <th className="text-center py-4 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Quotations Received</th>
                            <th className="text-center py-4 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Orders Placed</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stats.sub_users_stats.map((subUser) => (
                            <tr key={subUser.user_id} className="border-b border-slate-100 hover:bg-blue-50/30">
                              <td className="py-4 px-4">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center shadow-sm">
                                    <span className="text-sm font-bold text-white">
                                      {subUser.full_name?.charAt(0)?.toUpperCase()}
                                    </span>
                                  </div>
                                  <div>
                                    <p className="font-semibold text-slate-900">{subUser.full_name}</p>
                                    <p className="text-xs text-slate-500">{subUser.email}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="py-4 px-4 text-center">
                                <span className="inline-flex items-center justify-center w-10 h-10 bg-blue-50 rounded-xl text-sm font-bold text-blue-700">
                                  {subUser.rfqs_count}
                                </span>
                              </td>
                              <td className="py-4 px-4 text-center">
                                <span className="inline-flex items-center justify-center w-10 h-10 bg-amber-50 rounded-xl text-sm font-bold text-amber-700">
                                  {subUser.quotations_count}
                                </span>
                              </td>
                              <td className="py-4 px-4 text-center">
                                <span className="inline-flex items-center justify-center w-10 h-10 bg-emerald-50 rounded-xl text-sm font-bold text-emerald-700">
                                  {subUser.orders_count}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </main>
        </div>
      </div>
    );
  }

  // Vendor Dashboard
  if (user?.user_type === 'vendor') {
    // ── KPI Calculations ──
    const availableRfqs = rfqs.filter(r => r.status === 'sent').length;
    const submittedQuotes = quotations.filter(q => q.status === 'submitted').length;
    const acceptedQuotes = quotations.filter(q => q.status === 'accepted').length;
    const rejectedQuotes = quotations.filter(q => q.status === 'rejected').length;
    const underReviewQuotes = quotations.filter(q => q.status === 'submitted' || q.status === 'pending').length;
    const totalQuotes = quotations.length;
    const winRate = totalQuotes > 0 ? Math.round((acceptedQuotes / totalQuotes) * 100) : 0;
    
    const activeOrders = orders.filter(o => !['completed', 'cancelled'].includes(o.order_status)).length;
    const completedOrders = orders.filter(o => o.order_status === 'completed').length;
    const totalRevenue = orders.filter(o => o.order_status === 'completed').reduce((sum, o) => sum + (o.vendor_payout || 0), 0);
    const wonThisMonth = orders
      .filter(o => o.order_status !== 'cancelled' && new Date(o.created_at).getMonth() === new Date().getMonth())
      .reduce((sum, o) => sum + (o.vendor_payout || o.total_amount || 0), 0);
    const pipelineValue = quotations
      .filter(q => q.status === 'submitted' || q.status === 'pending')
      .reduce((sum, q) => sum + (q.total_amount || 0), 0);

    // ── Expiring / Matched RFPs ──
    const now = new Date();
    const expiringRfqs = rfqs.filter(r => {
      if (r.status !== 'sent') return false;
      const deadline = new Date(r.deadline);
      const hoursLeft = (deadline - now) / (1000 * 60 * 60);
      return hoursLeft > 0 && hoursLeft <= 48;
    });

    const ordersNeedingAction = orders.filter(o => 
      o.order_status === 'po_uploaded' || 
      o.order_status === 'pending_execution' || 
      o.order_status === 'executed'
    );

    const getNextAction = (status) => {
      const actions = {
        po_uploaded: 'Start Execution',
        pending_execution: 'Execute & Deliver',
        executed: 'Upload Invoice',
        invoiced: 'Awaiting Payment',
        po_pending: 'Awaiting Client PO',
      };
      return actions[status] || 'View Details';
    };

    const getDeadlineDays = (order) => {
      const created = new Date(order.created_at);
      const estDays = 14; // default estimate
      const dueDate = new Date(created.getTime() + estDays * 86400000);
      const diff = Math.ceil((dueDate - now) / 86400000);
      return diff > 0 ? `${diff} days` : 'Overdue';
    };

    return (
      <div className="min-h-screen bg-slate-50" data-testid="vendor-dashboard">
        <Navbar />
        <div className="flex">
          <Sidebar />
          <div className="flex-1 p-4 sm:p-6 lg:p-8 pb-20 lg:pb-8">
            <div className="max-w-7xl mx-auto space-y-6">

              {/* ── HEADER ── */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight">
                    Welcome back, {user?.full_name?.split(' ')[0]}
                  </h1>
                  <p className="text-slate-500 mt-1">
                    {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                  </p>
                </div>
                <Button 
                  onClick={() => navigate('/marketplace/quotations')}
                  className="bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-600/20"
                  data-testid="browse-rfps-btn"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Browse Opportunities
                </Button>
              </div>

              {/* Vendor Mobile Quick Actions */}
              <div className="grid grid-cols-4 gap-2 lg:hidden" data-testid="vendor-mobile-quick-actions">
                <button
                  onClick={() => navigate('/marketplace/rfqs')}
                  className="relative flex flex-col items-center gap-1 p-3 bg-white rounded-xl shadow-sm border border-slate-100 active:scale-95 transition-transform"
                  data-testid="vendor-quick-rfqs"
                >
                  <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                    <FileText className="w-5 h-5 text-blue-600" />
                  </div>
                  {badges.rfqs > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">{badges.rfqs}</span>
                  )}
                  <span className="text-[10px] font-medium text-slate-600">RFPs</span>
                </button>
                <button
                  onClick={() => navigate('/marketplace/orders')}
                  className="relative flex flex-col items-center gap-1 p-3 bg-white rounded-xl shadow-sm border border-slate-100 active:scale-95 transition-transform"
                  data-testid="vendor-quick-orders"
                >
                  <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                    <Package className="w-5 h-5 text-amber-600" />
                  </div>
                  {badges.orders > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">{badges.orders}</span>
                  )}
                  <span className="text-[10px] font-medium text-slate-600">Orders</span>
                </button>
                <button
                  onClick={() => navigate('/marketplace/messages')}
                  className="relative flex flex-col items-center gap-1 p-3 bg-white rounded-xl shadow-sm border border-slate-100 active:scale-95 transition-transform"
                  data-testid="vendor-quick-messages"
                >
                  <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-emerald-600" />
                  </div>
                  {badges.messages > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">{badges.messages}</span>
                  )}
                  <span className="text-[10px] font-medium text-slate-600">Messages</span>
                </button>
                <button
                  onClick={() => navigate('/marketplace/notifications')}
                  className="relative flex flex-col items-center gap-1 p-3 bg-white rounded-xl shadow-sm border border-slate-100 active:scale-95 transition-transform"
                  data-testid="vendor-quick-alerts"
                >
                  <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center">
                    <Bell className="w-5 h-5 text-violet-600" />
                  </div>
                  {badges.notifications > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">{badges.notifications}</span>
                  )}
                  <span className="text-[10px] font-medium text-slate-600">Alerts</span>
                </button>
              </div>

              {/* ── 1. TOP KPI BAR (Revenue Focus) ── */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="vendor-kpi-bar">
                <StatCard
                  title="Pipeline Value"
                  value={formatCurrency(pipelineValue)}
                  subtitle={`${underReviewQuotes} pending proposals`}
                  icon={TrendingUp}
                  color="blue"
                  onClick={() => navigate('/marketplace/quotations')}
                />
                <StatCard
                  title="Won This Month"
                  value={formatCurrency(wonThisMonth)}
                  subtitle={`${orders.filter(o => new Date(o.created_at).getMonth() === now.getMonth()).length} orders`}
                  icon={DollarSign}
                  color="emerald"
                  onClick={() => navigate('/marketplace/orders')}
                />
                <StatCard
                  title="Win Rate"
                  value={`${winRate}%`}
                  subtitle={`${acceptedQuotes} won of ${totalQuotes}`}
                  icon={Percent}
                  color="amber"
                />
                <StatCard
                  title="Active RFPs"
                  value={availableRfqs}
                  subtitle="Open opportunities"
                  icon={FileText}
                  color="indigo"
                  onClick={() => navigate('/marketplace/quotations')}
                />
              </div>

              {/* ── 2. ACTION SECTION ── */}
              {(expiringRfqs.length > 0 || ordersNeedingAction.length > 0) && (
                <div className="flex flex-col sm:flex-row gap-3" data-testid="action-alerts">
                  {expiringRfqs.length > 0 && (
                    <Card className="flex-1 border-0 bg-gradient-to-r from-red-50 to-orange-50 shadow-sm">
                      <CardContent className="p-4 flex items-center gap-3">
                        <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center shrink-0">
                          <Clock className="w-5 h-5 text-red-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-red-800 text-sm">{expiringRfqs.length} RFP{expiringRfqs.length > 1 ? 's' : ''} expiring in 48 hours</p>
                          <p className="text-xs text-red-600 truncate">Submit proposals before deadline</p>
                        </div>
                        <Button size="sm" variant="outline" className="border-red-200 text-red-700 hover:bg-red-100 shrink-0" onClick={() => navigate('/marketplace/quotations')}>
                          View Now
                        </Button>
                      </CardContent>
                    </Card>
                  )}
                  {ordersNeedingAction.length > 0 && (
                    <Card className="flex-1 border-0 bg-gradient-to-r from-blue-50 to-indigo-50 shadow-sm">
                      <CardContent className="p-4 flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center shrink-0">
                          <Zap className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-blue-800 text-sm">{ordersNeedingAction.length} project{ordersNeedingAction.length > 1 ? 's' : ''} need action</p>
                          <p className="text-xs text-blue-600 truncate">Keep your projects on track</p>
                        </div>
                        <Button size="sm" variant="outline" className="border-blue-200 text-blue-700 hover:bg-blue-100 shrink-0" onClick={() => navigate('/marketplace/orders')}>
                          View
                        </Button>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}

              {/* ── 3. OPPORTUNITIES (RFP Marketplace) ── */}
              <Card className="border-0 shadow-sm" data-testid="opportunities-section">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg font-bold">Opportunities</CardTitle>
                      <CardDescription>Available RFPs matching your services</CardDescription>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => navigate('/marketplace/quotations')} className="text-blue-600 hover:text-blue-700">
                      View All <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {rfqs.filter(r => r.status === 'sent').length === 0 ? (
                    <div className="text-center py-8">
                      <FileText className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                      <p className="text-slate-500">No open opportunities right now</p>
                      <p className="text-sm text-slate-400 mt-1">Check back later for new RFPs</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {rfqs.filter(r => r.status === 'sent').slice(0, 5).map((rfq) => {
                        const deadline = new Date(rfq.deadline);
                        const daysLeft = Math.ceil((deadline - now) / 86400000);
                        const hoursLeft = Math.max(0, Math.floor((deadline - now) / (1000 * 60 * 60)));
                        const minutesLeft = Math.max(0, Math.floor(((deadline - now) % (1000 * 60 * 60)) / (1000 * 60)));
                        const alreadyQuoted = quotations.some(q => q.rfq_id === rfq.id);
                        const quoteCount = rfq.quotations_count || rfq.quotations_received || 0;
                        const isUrgent = daysLeft <= 2 && daysLeft > 0;
                        const isExpired = daysLeft <= 0;
                        return (
                          <div key={rfq.id} className={`flex items-center gap-4 p-3 rounded-xl border transition-all cursor-pointer group ${isUrgent ? 'border-red-200 bg-red-50/30 hover:border-red-300' : 'border-slate-100 hover:border-blue-200 hover:bg-blue-50/30'}`} onClick={() => navigate(`/marketplace/rfq/${rfq.id}`)} data-testid={`opportunity-${rfq.id}`}>
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${isUrgent ? 'bg-red-100' : 'bg-blue-50'}`}>
                              <FileText className={`w-5 h-5 ${isUrgent ? 'text-red-600' : 'text-blue-600'}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-slate-900 text-sm truncate">{rfq.title}</p>
                              <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 flex-wrap">
                                <span>Budget: {rfq.budget ? formatCurrency(rfq.budget, rfq.currency) : 'N/A'}</span>
                                {rfq.mode === 'confidential' && (
                                  <span className="flex items-center gap-1 text-amber-600 font-medium">
                                    <Lock className="w-3 h-3" />Confidential
                                  </span>
                                )}
                                {quoteCount > 0 && (
                                  <span className="flex items-center gap-1 text-amber-600 font-medium" data-testid={`quote-count-${rfq.id}`}>
                                    <Users className="w-3 h-3" />{quoteCount} proposal{quoteCount > 1 ? 's' : ''}
                                  </span>
                                )}
                              </div>
                              {/* Countdown */}
                              <div className="mt-1" data-testid={`countdown-${rfq.id}`}>
                                {isExpired ? (
                                  <span className="text-xs font-semibold text-red-600">Expired</span>
                                ) : isUrgent ? (
                                  <span className="text-xs font-semibold text-red-600 flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {hoursLeft}h {minutesLeft}m remaining
                                  </span>
                                ) : (
                                  <span className="text-xs font-medium text-slate-500">
                                    {daysLeft} day{daysLeft > 1 ? 's' : ''} left
                                  </span>
                                )}
                              </div>
                            </div>
                            {alreadyQuoted ? (
                              <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs shrink-0">Submitted</Badge>
                            ) : (
                              <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-xs shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                                Submit Proposal
                              </Button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* ── 4. MY PROPOSALS TRACKING + 5. PROJECT EXECUTION ── */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* My Proposals Tracking */}
                <Card className="border-0 shadow-sm" data-testid="proposals-tracking">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg font-bold">My Proposals</CardTitle>
                        <CardDescription>Track your submitted proposals</CardDescription>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => navigate('/marketplace/quotations')} className="text-blue-600">
                        View All <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Pipeline Summary */}
                    <div className="grid grid-cols-4 gap-2 mb-4">
                      {[
                        { label: 'Submitted', count: submittedQuotes, color: 'bg-amber-500' },
                        { label: 'Review', count: underReviewQuotes, color: 'bg-blue-500' },
                        { label: 'Won', count: acceptedQuotes, color: 'bg-emerald-500' },
                        { label: 'Lost', count: rejectedQuotes, color: 'bg-slate-400' },
                      ].map((s) => (
                        <div key={s.label} className="text-center p-2 rounded-lg bg-slate-50">
                          <div className={`w-2 h-2 rounded-full ${s.color} mx-auto mb-1`} />
                          <p className="text-lg font-bold text-slate-900">{s.count}</p>
                          <p className="text-[10px] text-slate-500 uppercase tracking-wider">{s.label}</p>
                        </div>
                      ))}
                    </div>

                    {quotations.length === 0 ? (
                      <div className="text-center py-4">
                        <p className="text-sm text-slate-400">No proposals yet</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {quotations.slice(0, 5).map((q) => (
                          <div key={q.id} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => navigate('/marketplace/quotations')} data-testid={`proposal-${q.id}`}>
                            <div className={`w-2 h-2 rounded-full shrink-0 ${q.status === 'accepted' ? 'bg-emerald-500' : q.status === 'rejected' ? 'bg-red-400' : q.status === 'submitted' ? 'bg-amber-500' : 'bg-blue-500'}`} />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-800 truncate">{q.rfq_title || q.quotation_number}</p>
                            </div>
                            <Badge className={`${getStatusColor(q.status)} border text-[10px] shrink-0`}>
                              {q.status === 'accepted' ? 'Won' : q.status === 'rejected' ? 'Lost' : q.status?.replace(/_/g, ' ')}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Project Execution */}
                <Card className="border-0 shadow-sm" data-testid="project-execution">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg font-bold">Project Execution</CardTitle>
                        <CardDescription>Active projects & next actions</CardDescription>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => navigate('/marketplace/orders')} className="text-blue-600">
                        View All <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {orders.filter(o => !['completed', 'cancelled'].includes(o.order_status)).length === 0 ? (
                      <div className="text-center py-8">
                        <Package className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                        <p className="text-sm text-slate-500">No active projects</p>
                        <p className="text-xs text-slate-400 mt-1">Win a proposal to start</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {orders.filter(o => !['completed', 'cancelled'].includes(o.order_status)).slice(0, 5).map((order) => (
                          <div key={order.id} className="p-3 rounded-xl border border-slate-100 hover:border-indigo-200 hover:bg-indigo-50/20 transition-all cursor-pointer" onClick={() => navigate(`/marketplace/orders/${order.id}`)} data-testid={`project-${order.id}`}>
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className="font-medium text-sm text-slate-900 truncate">{order.rfq_title || order.order_number}</p>
                                <p className="text-xs text-slate-500 mt-0.5">{order.pharma_company_name || 'Client'}</p>
                              </div>
                              <Badge className={`${getStatusColor(order.order_status)} border text-[10px] shrink-0`}>
                                {order.order_status?.replace(/_/g, ' ')}
                              </Badge>
                            </div>
                            <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
                              <div className="flex items-center gap-1 text-xs">
                                <Zap className="w-3 h-3 text-blue-600" />
                                <span className="text-blue-700 font-medium">{getNextAction(order.order_status)}</span>
                              </div>
                              <span className="text-xs text-slate-400">{getDeadlineDays(order)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* ── 6. PERFORMANCE INSIGHTS ── */}
              <Card className="border-0 shadow-sm" data-testid="performance-insights">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-bold">Performance Insights</CardTitle>
                  <CardDescription>Your competitive edge at a glance</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-xl">
                      <div className="flex items-center gap-2 mb-2">
                        <Percent className="w-4 h-4 text-blue-600" />
                        <span className="text-sm font-medium text-blue-700">Win Rate</span>
                      </div>
                      <p className="text-3xl font-bold text-blue-900">{winRate}%</p>
                      <p className="text-xs text-blue-600 mt-1">{acceptedQuotes} of {totalQuotes} proposals</p>
                    </div>
                    <div className="p-4 bg-gradient-to-br from-emerald-50 to-emerald-100/50 rounded-xl">
                      <div className="flex items-center gap-2 mb-2">
                        <DollarSign className="w-4 h-4 text-emerald-600" />
                        <span className="text-sm font-medium text-emerald-700">Total Revenue</span>
                      </div>
                      <p className="text-3xl font-bold text-emerald-900">{formatCurrency(totalRevenue)}</p>
                      <p className="text-xs text-emerald-600 mt-1">{completedOrders} projects delivered</p>
                    </div>
                    <div className="p-4 bg-gradient-to-br from-amber-50 to-amber-100/50 rounded-xl">
                      <div className="flex items-center gap-2 mb-2">
                        <Star className="w-4 h-4 text-amber-600" />
                        <span className="text-sm font-medium text-amber-700">Client Rating</span>
                      </div>
                      <p className="text-3xl font-bold text-amber-900">{stats?.average_rating?.toFixed(1) || '—'}</p>
                      <p className="text-xs text-amber-600 mt-1">{stats?.total_reviews || 0} reviews</p>
                    </div>
                    <div className="p-4 bg-gradient-to-br from-indigo-50 to-indigo-100/50 rounded-xl">
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="w-4 h-4 text-indigo-600" />
                        <span className="text-sm font-medium text-indigo-700">Active Pipeline</span>
                      </div>
                      <p className="text-3xl font-bold text-indigo-900">{activeOrders + underReviewQuotes}</p>
                      <p className="text-xs text-indigo-600 mt-1">{activeOrders} projects + {underReviewQuotes} proposals</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* ── 7. SMART NOTIFICATIONS ── */}
              {notifications.length > 0 && (
                <Card className="border-0 shadow-sm" data-testid="smart-notifications">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg font-bold">Recent Alerts</CardTitle>
                        <CardDescription>Important updates for you</CardDescription>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => navigate('/marketplace/notifications')} className="text-blue-600">
                        View All <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {notifications.slice(0, 5).map((notif, idx) => (
                        <div key={notif.id || idx} className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => navigate('/marketplace/notifications')}>
                          <div className={`w-2 h-2 rounded-full shrink-0 ${!notif.is_read ? 'bg-blue-600' : 'bg-slate-300'}`} />
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm truncate ${!notif.is_read ? 'font-semibold text-slate-900' : 'text-slate-600'}`}>{notif.title || notif.message}</p>
                          </div>
                          <span className="text-xs text-slate-400 shrink-0">
                            {notif.created_at ? new Date(notif.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

            </div>
          </div>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  if (user?.user_type === 'admin') {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
        <Navbar />
        <div className="flex">
          <Sidebar />
          <div className="flex-1 p-4 sm:p-6 lg:p-8 pb-20 lg:pb-8">
            <div className="max-w-7xl mx-auto space-y-6">
              {/* Admin Header */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Admin Dashboard</h1>
                  <p className="text-slate-500 mt-1">Platform overview and management</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="px-3 py-1">
                    <CircleDot className="w-3 h-3 mr-1 text-emerald-500" />
                    System Online
                  </Badge>
                </div>
              </div>

              {/* Alert for Pending Approvals */}
              {adminStats?.pendingApprovals > 0 && (
                <Card className="border-amber-200 bg-amber-50/50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                        <ShieldCheck className="w-5 h-5 text-amber-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-amber-800">
                          {adminStats.pendingApprovals} vendor{adminStats.pendingApprovals > 1 ? 's' : ''} pending verification
                        </p>
                        <p className="text-sm text-amber-700">Review documents and approve to onboard vendors</p>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="border-amber-300 text-amber-700 hover:bg-amber-100"
                        onClick={() => navigate('/marketplace/admin/vendor-verification')}
                      >
                        Review Now
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Platform Overview Metrics */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                  title="Total Vendors"
                  value={adminStats?.totalVendors || 0}
                  subtitle={`${adminStats?.activeVendors || 0} active`}
                  icon={Building2}
                  color="blue"
                  onClick={() => navigate('/marketplace/admin/vendors')}
                />
                <StatCard
                  title="Total Customers"
                  value={adminStats?.totalCustomers || 0}
                  subtitle={`${adminStats?.activeCustomers || 0} active`}
                  icon={Users}
                  color="emerald"
                  onClick={() => navigate('/marketplace/admin/customers')}
                />
                <StatCard
                  title="Total Orders"
                  value={adminStats?.totalOrders || orders.length}
                  subtitle={`${adminStats?.activeOrders || 0} active`}
                  icon={Package}
                  color="indigo"
                  onClick={() => navigate('/marketplace/admin/orders')}
                />
                <StatCard
                  title="Platform Revenue"
                  value={formatCurrency(adminStats?.totalCommission || 0)}
                  subtitle="Commission earned"
                  icon={DollarSign}
                  color="violet"
                  onClick={() => navigate('/marketplace/reports')}
                />
              </div>

              {/* Secondary Metrics Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-amber-50 to-amber-100/50 border-amber-200">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-amber-700">Pending Approvals</p>
                        <p className="text-3xl font-bold text-amber-900 mt-1">{adminStats?.pendingApprovals || 0}</p>
                        <p className="text-xs text-amber-600 mt-1">Vendors awaiting review</p>
                      </div>
                      <ShieldCheck className="w-10 h-10 text-amber-500/50" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-blue-50 to-blue-100/50 border-blue-200">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-blue-700">Active RFPs</p>
                        <p className="text-3xl font-bold text-blue-900 mt-1">{rfqs.filter(r => r.status === 'sent').length}</p>
                        <p className="text-xs text-blue-600 mt-1">Open for quotations</p>
                      </div>
                      <FileText className="w-10 h-10 text-blue-500/50" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 border-emerald-200">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-emerald-700">Completed Orders</p>
                        <p className="text-3xl font-bold text-emerald-900 mt-1">{adminStats?.completedOrders || 0}</p>
                        <p className="text-xs text-emerald-600 mt-1">Successfully delivered</p>
                      </div>
                      <CheckCircle2 className="w-10 h-10 text-emerald-500/50" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100/50 border-indigo-200">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-indigo-700">Total GMV</p>
                        <p className="text-3xl font-bold text-indigo-900 mt-1">{formatCurrency(adminStats?.totalRevenue || 0)}</p>
                        <p className="text-xs text-indigo-600 mt-1">Gross merchandise value</p>
                      </div>
                      <TrendingUp className="w-10 h-10 text-indigo-500/50" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Quick Admin Actions */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <QuickActionCard
                  title="Verify Vendors"
                  description="Review pending applications"
                  icon={ShieldCheck}
                  color="blue"
                  onClick={() => navigate('/marketplace/admin/vendor-verification')}
                />
                <QuickActionCard
                  title="User Management"
                  description="Manage all users"
                  icon={Users}
                  color="emerald"
                  onClick={() => navigate('/marketplace/admin/user-management')}
                />
                <QuickActionCard
                  title="Order Management"
                  description="Monitor all orders"
                  icon={Package}
                  color="indigo"
                  onClick={() => navigate('/marketplace/admin/orders')}
                />
                <QuickActionCard
                  title="AI Design Settings"
                  description="Configure AI features"
                  icon={Sparkles}
                  color="violet"
                  onClick={() => navigate('/marketplace/admin/design-settings')}
                />
              </div>

              {/* Recent Activity Tables */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Recent RFPs */}
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg">Recent RFPs</CardTitle>
                        <CardDescription>Platform-wide RFP activity</CardDescription>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => navigate('/marketplace/admin/rfqs')}>
                        View All <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {rfqs.length === 0 ? (
                      <div className="text-center py-8">
                        <FileText className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                        <p className="text-slate-500">No RFPs yet</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {rfqs.slice(0, 5).map((rfq) => (
                          <ActivityItem
                            key={rfq.id}
                            title={rfq.title}
                            subtitle={`${rfq.rfq_number} • ${formatDate(rfq.created_at)}`}
                            status={rfq.status}
                            amount={rfq.budget}
                            currency={rfq.currency}
                            icon={FileText}
                            onClick={() => navigate(`/marketplace/admin/rfq/${rfq.id}`)}
                          />
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Recent Orders */}
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg">Recent Orders</CardTitle>
                        <CardDescription>Platform-wide order activity</CardDescription>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => navigate('/marketplace/admin/orders')}>
                        View All <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {orders.length === 0 ? (
                      <div className="text-center py-8">
                        <Package className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                        <p className="text-slate-500">No orders yet</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {orders.slice(0, 5).map((order) => (
                          <ActivityItem
                            key={order.id}
                            title={order.rfq_title || order.title || 'Untitled RFP'}
                            subtitle={`${order.order_number} • ${order.vendor_company_name || 'Vendor'}`}
                            status={order.order_status}
                            amount={order.total_amount}
                            currency={order.currency}
                            icon={Package}
                            onClick={() => navigate(`/marketplace/orders/${order.id}`)}
                          />
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* System Health Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="w-5 h-5" />
                    Platform Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {[
                      { label: 'Vendors', value: adminStats?.totalVendors || 0, active: adminStats?.activeVendors || 0 },
                      { label: 'Customers', value: adminStats?.totalCustomers || 0, active: adminStats?.activeCustomers || 0 },
                      { label: 'RFPs', value: rfqs.length, active: rfqs.filter(r => r.status === 'sent').length },
                      { label: 'Orders', value: adminStats?.totalOrders || orders.length, active: adminStats?.activeOrders || 0 },
                      { label: 'Completed', value: adminStats?.completedOrders || 0, active: null },
                      { label: 'Pending Review', value: adminStats?.pendingApprovals || 0, active: null },
                    ].map((item, idx) => (
                      <div key={idx} className="text-center p-4 bg-slate-50 rounded-lg">
                        <p className="text-2xl font-bold text-slate-900">{item.value}</p>
                        <p className="text-sm text-slate-500">{item.label}</p>
                        {item.active !== null && (
                          <p className="text-xs text-emerald-600 mt-1">{item.active} active</p>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Default fallback
  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="flex">
        <Sidebar />
        <div className="flex-1 p-8 flex items-center justify-center">
          <Card>
            <CardContent className="p-8 text-center">
              <AlertCircle className="w-12 h-12 mx-auto text-slate-400 mb-4" />
              <h2 className="text-xl font-semibold text-slate-900 mb-2">Dashboard Not Available</h2>
              <p className="text-slate-600">Your user role is not recognized.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
